import { Capacitor } from '@capacitor/core';
import { useOfflineSession } from '../offline/OfflineSessionContext';
import useOfflineConversation from '../offline/useOfflineConversation';
import { saveConversationSnapshot } from '../offline/conversationRepository';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import renderMarkdown from '../renderMarkdown';
import axios from 'axios';
import ChatHistory from './ChatHistory';
import FinanceReviewCard from './FinanceReviewCard';
import SidebarNav from './SidebarNav';
import ChatComposer from './ChatComposer';
import { agentForDomain, domainForAgent, modeForAgent } from '../chatDomains';
import { DomainPicker, DomainTasks } from './ChatDomains';
import WorkspaceDialog from './WorkspaceDialog';
import WeatherCard from './WeatherCard';
import { WhatsAppInvitation } from './WhatsAppAccess';
import { FaTimes, FaWhatsapp } from 'react-icons/fa';
import CreditQuestionCard from './CreditQuestionCard';
import { FiEdit3 } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { AUTH_API_URL as API_URL, BACKEND_MAIN_URL } from '../config';
import { projectAuthHeaders } from '../projectAuth';
import { useActiveChatDomain } from '../ActiveChatDomainContext';
import authHeaders from '../authHeaders';
//import MetricComparisonTable from './MetricComparisonTable'; // ⬅️ nouveau

const STORAGE_KEY = 'daily-usage';
const VISION_STORAGE_KEY = 'vision-usage';
const SUBSCRIPTION_URL = '/upgrade';
const ACTIVE_CHAT_AGENT_STORAGE_KEY = 'active_chat_agent_id';

// Serialize the visible conversation for stateless image/lab endpoints so the
// RAG chain receives prior turns (default chat history lives server-side via
// websocket; vision/plantnet/labo are HTTP and have no per-session memory).
const serializeChatHistory = (messages, maxMessages = 20) => {
  if (!Array.isArray(messages) || messages.length === 0) return '[]';
  const trimmed = messages.slice(-maxMessages);
  return JSON.stringify(
    trimmed
      .map((m) => ({
        role: m.isUser ? 'user' : 'ai',
        content: typeof m.content === 'string' ? m.content : '',
      }))
      .filter((m) => m.content.trim())
  );
};

// #94: a small downscaled JPEG thumbnail (data URL) stored on the user message
// so the attached image is still visible when the conversation is reopened.
// Only images are thumbnailed; returns null for PDFs/other or on any failure.
const fileToThumbnail = (file, maxDim = 240, quality = 0.6) => new Promise((resolve) => {
  if (!file || !file.type || !file.type.startsWith('image/')) { resolve(null); return; }
  // Read as a data: URL, NOT URL.createObjectURL: the page CSP is
  // `img-src 'self' data: https://*`, which blocks blob: URLs. With a blob the
  // image silently failed to load, this returned null, and the chat showed the
  // "[Image sent — …]" placeholder instead of the picture.
  const reader = new FileReader();
  reader.onerror = () => resolve(null);
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = reader.result;
  };
  try { reader.readAsDataURL(file); } catch { resolve(null); }
});

const getMaxQuestions = (plan) => {
  if (plan === 'freemium') return 3;
  if (plan === 'standard') return 10000;
  if (plan === 'finance') return 10000;
  if (plan === 'premium') return 10000;
  return 3;
};

// Product decision: hide LLM-emitted reference sections ("Pour aller plus
// loin" / "To go further" / "Para ir más allá" / "Useful References" etc.)
// from user-facing answers, both for live streams AND for restored history
// (older stored conversations still have the section baked in). To re-enable,
// remove the stripReferenceSections() call at the render site.
const _REFERENCE_LABELS =
  'Pour aller plus loin|To go further|Para ir m\\u00e1s all\\u00e1'
  + '|Useful References|R\\u00e9f\\u00e9rences utiles|Referencias \\u00fatiles'
  + '|References|R\\u00e9f\\u00e9rences|Referencias';

const _REF_STRIP_PATTERNS = [
  // Block heading (### / ** / plain) followed by content until next section boundary.
  new RegExp(
    '\\n\\s*(?:#{1,6}\\s+|\\*\\*\\s*)?(?:' + _REFERENCE_LABELS + ')'
    + '\\s*(?:\\*\\*)?\\s*:?\\s*\\n'
    + '.*?(?='
    + '\\n\\s*#{1,6}\\s'
    + '|\\n\\s*\\*\\*?\\s*(?:Question|Invite|Next Step|Prochaine \\u00e9tape'
    + '|Pr\\u00f3ximo paso|Siguiente paso|Pregunta|Invitaci\\u00f3n)'
    + '|\\n\\s*(?:Question pour vous|Question for you|Pregunta para ti'
    + '|Invite for Context|Invitaci\\u00f3n para contexto|Next Step'
    + '|Prochaine \\u00e9tape|Pr\\u00f3ximo paso)\\b'
    + '|$)',
    'gis',
  ),
  // Same block but the label and its content live on a single line.
  new RegExp(
    '\\n\\s*(?:\\*\\*\\s*)?(?:' + _REFERENCE_LABELS + ')'
    + '\\s*(?:\\*\\*)?\\s*:\\s[^\\n]*',
    'gi',
  ),
  // "References (STRICT)" legacy plantnet heading.
  new RegExp('\\n\\s*References \\(STRICT\\).*?(?=\\n\\s*###\\s|$)', 'gis'),
  // Evidence-panel single-line footer.
  new RegExp('\\n?\\*\\*R\\u00e9f\\u00e9rences[^*]*\\*\\*\\s*:[^\\n]*', 'gi'),
  // Numbered "1. Useful References: ..." block from DEFAULT_PROMPT closing.
  new RegExp(
    '\\n\\s*\\d+\\.\\s*(?:Useful References|R\\u00e9f\\u00e9rences utiles'
    + '|Referencias \\u00fatiles|Pour aller plus loin|To go further'
    + '|Para ir m\\u00e1s all\\u00e1)\\s*:.*?(?=\\n\\s*\\d+\\.\\s|$)',
    'gis',
  ),
];

function stripReferenceSections(text) {
  if (!text) return text;
  let out = text;
  for (const pattern of _REF_STRIP_PATTERNS) {
    out = out.replace(pattern, '\n');
  }
  out = out.replace(/\n\s*---\s*(?=\n|$)/g, '\n');
  out = out.replace(/\n{3,}/g, '\n\n');
  return out.trim() + '\n';
}

function splitTransparencySection(md) {
  if (!md) return { coreMarkdown: '', transparencyMarkdown: '' };
  const idx = md.search(/^\s*###\s+(?:Transparence\s+—\s+preuves\s+scientifiques|Sources)/im);
  if (idx === -1) {
    const idx2 = md.search(/^\s*###\s+Transparence/im);
    if (idx2 === -1) {
      return { coreMarkdown: md, transparencyMarkdown: '' };
    }
    return {
      coreMarkdown: md.slice(0, idx2).trim(),
      transparencyMarkdown: md.slice(idx2).trim(),
    };
  }
  return {
    coreMarkdown: md.slice(0, idx).trim(),
    transparencyMarkdown: md.slice(idx).trim(),
  };
}

function inferConversationAgentId(conversation) {
  if (conversation?.agent_id) return conversation.agent_id;

  const messages = conversation?.messages || [];
  for (const message of messages) {
    if (message?.agent_id) return message.agent_id;
    if (message?.agentId) return message.agentId;
  }

  return 'default';
}

const todayISO = () => new Date().toISOString().slice(0, 10);
function getVisionUsage() {
  try {
    const raw = localStorage.getItem(VISION_STORAGE_KEY);
    if (!raw) return { date: todayISO(), count: 0 };
    const obj = JSON.parse(raw);
    const date = obj?.date || todayISO();
    const count = obj?.count ?? 0;
    if (date !== todayISO()) return { date: todayISO(), count: 0 };
    return { date, count };
  } catch {
    return { date: todayISO(), count: 0 };
  }
}
function setVisionUsage(next) {
  localStorage.setItem(VISION_STORAGE_KEY, JSON.stringify(next));
}

/* ===== Tooltip Portal (au-dessus de tout) ===== */
const tooltipBoxStyle = {
  background: '#111827',
  color: '#fff',
  padding: '10px 12px',
  borderRadius: 8,
  fontSize: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
  width: 300,
};
const tooltipArrowStyle = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
  borderTop: '8px solid #111827',
};

function ClipTooltip({ anchorRef, show, children }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!show || !anchorRef?.current) return;
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.top - 12, left: r.left + r.width / 2 });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [show, anchorRef]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: 'translate(-50%, -100%)',
        zIndex: 100000, // très au-dessus
      }}
      role="tooltip"
    >
      <div style={{ position: 'relative' }}>
        <div style={tooltipBoxStyle}>{children}</div>
        <div style={tooltipArrowStyle} />
      </div>
    </div>,
    document.body
  );
}
void ClipTooltip;

/** ===== Composant principal Chat ===== **/
const Chat = ({
  messages,
  isLoading,
  onQuestionSubmit,
  onConversationLoad,
  onAgentSessionReset,
  onChatHistorySync,
  streamedResponse,
  responseComplete,
  onLogout,
  embedded = false,
  embeddedProject = null,
  embeddedConversation,
  embeddedResetKey,
  prefillQuestion,
  onConversationSaved,
  onDomainChange,
  marketAvailable = false,
}) => {
  const { offlineReadOnly, chatBackendUnavailable } = useOfflineSession();
  const nativeOffline = Capacitor.isNativePlatform();
  const networkBlocked = offlineReadOnly || chatBackendUnavailable || (nativeOffline && navigator.onLine === false);
  const networkBlockedRef = useRef(networkBlocked);
  networkBlockedRef.current = networkBlocked;
  const location = useLocation();
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  useEffect(() => {
    if (prefillQuestion?.text) setQuestion(prefillQuestion.text);
  }, [prefillQuestion]);
  const [selectedOfflineState, setSelectedOfflineState] = useState('');
  const [offlineNoticeDismissed, setOfflineNoticeDismissed] = useState(false);
  const { setDomain: setShellDomain } = useActiveChatDomain();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const cacheOpenedConversation = useCallback((conversation) => {
    if (nativeOffline) setSelectedProjectId(conversation?.projectId || null);
    if (!nativeOffline || !conversation?.messages?.length) return;
    setOfflineNoticeDismissed(false);
    setSelectedOfflineState(conversation.interrupted ? 'interrupted' : '');
    const account = localStorage.getItem('userId');
    if (account) saveConversationSnapshot(account, conversation, { source: 'server' })
      .then((saved) => setSelectedOfflineState(saved.interrupted ? 'interrupted' : 'saved'))
      .catch(() => setSelectedOfflineState('error'));
  }, [nativeOffline]);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesPanelRef = useRef(null);
  const followLatestRef = useRef(true);
  useEffect(() => {
    const panel = messagesPanelRef.current;
    if (panel && followLatestRef.current) panel.scrollTop = panel.scrollHeight;
  }, [chatMessages]);
  const [conversationIdsByAgent, setConversationIdsByAgent] = useState({});
  const [limitReached, setLimitReached] = useState(false);
  const [userPlan, setUserPlan] = useState('freemium');
  const [userPlanLoaded, setUserPlanLoaded] = useState(!localStorage.getItem('token'));
  const [maxQuestions, setMaxQuestions] = useState(getMaxQuestions('freemium'));
  const [remaining, setRemaining] = useState(getMaxQuestions('freemium'));
  const [pendingSave, setPendingSave] = useState(false);
  const [isRouting, setIsRouting] = useState(false);
  const submitLockRef = useRef(false);
  // Runs after every commit: by then isLoading/isRouting/isAnalyzing reflect the
  // submit that took the lock, so the ordinary guards take over from here.
  useEffect(() => { submitLockRef.current = false; });
  // Last AI payload actually applied, so "did this change?" can be answered
  // without relying on when React chooses to run a state updater.
  const lastAppliedSignatureRef = useRef(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // Default-chat multi-file attachments. Each entry has the shape returned by
  // POST /api/chat/upload: { id, name, size, mime_type, char_count, extracted_text }.
  const [chatFiles, setChatFiles] = useState([]);
  const [pendingChatFiles, setPendingChatFiles] = useState([]);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const chatFilesInputRef = useRef(null);
  const CHAT_FILE_ACCEPT = "image/*,.pdf,.docx,.xlsx,.csv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,text/plain";

  // New State for Chat Modes
  const [domain, setDomain] = useState('agri');
  useEffect(() => { setShellDomain(domain); onDomainChange?.(domain); }, [domain, onDomainChange, setShellDomain]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showEvaluationPoints, setShowEvaluationPoints] = useState(false);
  const [chatMode, setChatMode] = useState('default'); // 'default', 'vision', 'plantnet', 'labo'
  // Answer length (issue #65). Persisted so the choice survives a reload.
  const [answerMode, setAnswerMode] = useState(
    () => localStorage.getItem('upya_answer_mode') === 'detailed' ? 'detailed' : 'flash'
  );
  const changeAnswerMode = (next) => {
    setAnswerMode(next);
    localStorage.setItem('upya_answer_mode', next);
  };
  const [activeProject, setActiveProject] = useState(null); // { id, name, instructions, files } | null
  const handledNavigationTimestampRef = useRef(null);
  const ignoredParentMessageRef = useRef(null);
  const latestParentMessageRef = useRef(null);
  const onConversationLoadRef = useRef(onConversationLoad);
  const onAgentSessionResetRef = useRef(onAgentSessionReset);
  const { t, i18n } = useTranslation();

  const token = localStorage.getItem('token');
  const hasFinanceAccess = userPlan === 'finance';
  // Only reveal the Finance tab once the plan is actually known AND allows it.
  // Gating on `userPlanLoaded` too stops the tab flashing for a freemium user
  // during the plan fetch. The server refuses the agent independently.
  const showFinanceDomain = userPlanLoaded && hasFinanceAccess;
  // Offline, the plan is unknown; keep the tab of a finance conversation opened
  // from the local history visible while it is read.
  const allowFinanceDomain = showFinanceDomain || (networkBlocked && domain === 'finance');
  // Finance tab: 'credit_agricole' only while the guided assessment is active
  // (chatMode==='credit'); otherwise 'finance_chat' (normal chat, finance lens).
  const currentAgentId = domain === 'market' ? 'market'
    : chatMode === 'credit' ? 'credit_agricole'
    : domain === 'finance' ? 'finance_chat'
    : 'default';
  const isFileMode = chatMode === 'vision' || chatMode === 'plantnet' || chatMode === 'labo';
  const conversationId = conversationIdsByAgent[currentAgentId] || null;
  // #84: id of the WhatsApp conversation on screen, to show where it comes from.
  const [whatsappConversationId, setWhatsappConversationId] = useState(null);

  useEffect(() => {
    latestParentMessageRef.current = messages?.[messages.length - 1] || null;
  }, [messages]);

  useEffect(() => {
    onConversationLoadRef.current = onConversationLoad;
  }, [onConversationLoad]);

  useEffect(() => {
    onAgentSessionResetRef.current = onAgentSessionReset;
  }, [onAgentSessionReset]);

  // Handle navigation state from MobileSidebar
  useEffect(() => {
    const navigationTimestamp = location.state?.timestamp;
    if (!navigationTimestamp || handledNavigationTimestampRef.current === navigationTimestamp) {
      return;
    }

    const { conversation } = location.state;
    const targetAgentId = location.state?.agentId || inferConversationAgentId(conversation);

    const targetIsFinance = targetAgentId === 'credit_agricole' || targetAgentId === 'finance_chat';
    if (targetIsFinance && !networkBlocked && !userPlanLoaded) {
      return;
    }

    handledNavigationTimestampRef.current = navigationTimestamp;

    if (targetIsFinance && !networkBlocked && !hasFinanceAccess) {
      setChatMode('default');
      setDomain('agri');
      setSelectedTask(null);
      setChatMessages([]); lastAppliedSignatureRef.current = null;
      onConversationLoad([], 'default');
      return;
    }

    const nextChatMode = modeForAgent(targetAgentId);

    setChatMode(nextChatMode);
    setDomain(domainForAgent(targetAgentId));
    setSelectedTask(null);
    followLatestRef.current = true;
    setQuestion('');
    setChatFiles([]);
    setFile(null);

    if (conversation === null) {
      setSelectedOfflineState('');
      setOfflineNoticeDismissed(false);
      setSelectedProjectId(null);
      if (!embedded) setActiveProject(null);
      setChatMessages([]); lastAppliedSignatureRef.current = null;
      setConversationIdsByAgent((prev) => ({ ...prev, [targetAgentId]: null }));
      onConversationLoad([], targetAgentId);
      onAgentSessionReset?.(targetAgentId);
    } else if (conversation) {
      cacheOpenedConversation(conversation);
      const msgs = conversation.messages || [];
      setChatMessages(msgs);
      setConversationIdsByAgent((prev) => ({ ...prev, [targetAgentId]: conversation.id }));
      onConversationLoad(msgs, targetAgentId);
      if (!embedded) {
        let projectId = conversation.projectId;
        try { projectId = projectId || JSON.parse(localStorage.getItem('projectChatLinks') || '{}')[conversation.id]; } catch {}
        setActiveProject(null);
        if (projectId && !networkBlocked) axios.get(`${BACKEND_MAIN_URL}/api/projects/${projectId}`, {
          headers: projectAuthHeaders(),
        }).then(({ data }) => {
          if (handledNavigationTimestampRef.current === navigationTimestamp) setActiveProject(data);
        }).catch(() => {});
      }
    }
  }, [embedded, hasFinanceAccess, location.state, onAgentSessionReset, onConversationLoad, userPlanLoaded, networkBlocked, cacheOpenedConversation]);

  useEffect(() => {
    if (!embedded) return;
    setActiveProject(embeddedProject || null);
  }, [embedded, embeddedProject]);

  useEffect(() => {
    if (!embedded || !embeddedResetKey) return;
    const latestParentMessage = latestParentMessageRef.current;
    ignoredParentMessageRef.current = latestParentMessage && !latestParentMessage.isUser ? latestParentMessage : null;
    setChatMode('default');
      setDomain('agri');
      setSelectedTask(null);
    setQuestion('');
    setChatFiles([]);
    setFile(null);
    followLatestRef.current = true;
    setChatMessages([]); lastAppliedSignatureRef.current = null;
    setConversationIdsByAgent({});
    onConversationLoadRef.current([], 'default');
    onAgentSessionResetRef.current?.('default');
  }, [embedded, embeddedResetKey]);

  // Load active project from ?projectId=... in the URL (set when arriving from ProjectPage)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectId = params.get('projectId') || (nativeOffline ? selectedProjectId : null);
    const initialQ = params.get('q');

    if (embedded) {
      return;
    }

    if (networkBlocked) return;
    if (!projectId) {
      setActiveProject(null);
      return;
    }

    const userId = localStorage.getItem('userId');
    axios
      .get(`${BACKEND_MAIN_URL}/api/projects/${projectId}`, {
        headers: { ...(userId ? { 'X-User-ID': userId } : {}), ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
      })
      .then((resp) => {
        setActiveProject(resp.data);
        if (initialQ) setQuestion(initialQ);
      })
      .catch((err) => {
        console.error('Failed to load project:', err);
        setActiveProject(null);
      });
  }, [embedded, location.search, networkBlocked, nativeOffline, selectedProjectId]);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isVisionStreaming, setIsVisionStreaming] = useState(false);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [isAnalyzingLab, setIsAnalyzingLab] = useState(false);
  // Vision-flow phase, driven by SSE "status" events ("vision" → vision call
  // running, "rag" → first token about to arrive). Used to pick a more
  // accurate spinner text instead of a static one.
  const [visionPhase, setVisionPhase] = useState('');
  const [plantnetOrgan, setPlantnetOrgan] = useState('auto');
  const [file, setFile] = useState(null);

  const fileInputRef = useRef();

  // Auto-growing question textareas (initial + in-chat).
  const questionInitialRef = useRef(null);
  const questionChatRef = useRef(null);
  const QUESTION_MAX_HEIGHT = 180;
  const autoSizeQuestion = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = parseFloat(window.getComputedStyle(el).maxHeight) || QUESTION_MAX_HEIGHT;
    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
  };
  useEffect(() => {
    const resize = () => {
      autoSizeQuestion(questionInitialRef.current);
      autoSizeQuestion(questionChatRef.current);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [question, chatMessages.length]);
  const handleQuestionKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !window.matchMedia('(pointer: coarse)').matches) {
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
    }
  };

  // Upsell vision
  const [showVisionUpsell, setShowVisionUpsell] = useState(false);

  // Tooltip states + refs pour les deux trombones
  const [showClipTip, setShowClipTip] = useState(false);
  const clipBtnRefTop = useRef(null);
  const clipBtnRefBottom = useRef(null);
  void showClipTip;
  void setShowClipTip;
  void clipBtnRefTop;
  void clipBtnRefBottom;

  const hasVisionAccess = userPlan === 'premium' || userPlan === 'vision' || userPlan === 'finance';
  const visionPlanLabel =
    userPlan === 'finance'
      ? t('subscription.finance_title', 'Upya Finance')
      : t('subscription.premium_title', 'Upya Vision');
  const latestMessage = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1] : null;
  const activeCreditQuestion =
    chatMode === 'credit' && latestMessage && !latestMessage.isUser
      ? latestMessage.creditQuestionnaire || null
      : null;
  const hasActiveCreditQuestion = Boolean(activeCreditQuestion);

  const canUseVisionNow = () => {
    if (hasVisionAccess) return true;
    const usage = getVisionUsage();
    return usage.count < 1;
  };

  useEffect(() => {
    localStorage.setItem(ACTIVE_CHAT_AGENT_STORAGE_KEY, currentAgentId);
  }, [currentAgentId]);

  const setConversationIdForAgent = useCallback((agentId, nextConversationId) => {
    setConversationIdsByAgent((prev) => ({ ...prev, [agentId]: nextConversationId }));
  }, []);

  useEffect(() => {
    if (!embedded || embeddedConversation === undefined) return;

    if (embeddedConversation === null) {
      const latestParentMessage = latestParentMessageRef.current;
      ignoredParentMessageRef.current = latestParentMessage && !latestParentMessage.isUser ? latestParentMessage : null;
      setChatMode('default');
      setDomain('agri');
      setSelectedTask(null);
      setQuestion('');
      setChatMessages([]); lastAppliedSignatureRef.current = null;
      setConversationIdForAgent('default', null);
      onConversationLoadRef.current([], 'default');
      onAgentSessionResetRef.current?.('default');
      return;
    }

    const targetAgentId = inferConversationAgentId(embeddedConversation);
    cacheOpenedConversation(embeddedConversation);
    const msgs = embeddedConversation?.messages || [];
    const latestParentMessage = latestParentMessageRef.current;
    ignoredParentMessageRef.current = latestParentMessage && !latestParentMessage.isUser ? latestParentMessage : null;
    setChatMode(modeForAgent(targetAgentId));
    setDomain(domainForAgent(targetAgentId));
    setSelectedTask(null);
    setQuestion('');
    setChatMessages(msgs);
    setConversationIdForAgent(targetAgentId, embeddedConversation.id);
    onConversationLoadRef.current(msgs, targetAgentId);
  }, [embedded, embeddedConversation, setConversationIdForAgent, cacheOpenedConversation]);

  const consumeVision = () => {
    if (hasVisionAccess) return;
    const usage = getVisionUsage();
    setVisionUsage({ date: todayISO(), count: Math.min(usage.count + 1, 1) });
  };

  const { checkpoint: checkpointOffline, persist: persistOffline, confirm: confirmOffline, storageState } = useOfflineConversation({
    enabled: nativeOffline, userId: localStorage.getItem('userId'), conversationId,
    messages: chatMessages, agentId: currentAgentId, projectId: activeProject?.id || selectedProjectId,
    busy: isLoading || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab,
  });
  useEffect(() => {
    if ((storageState || selectedOfflineState) !== 'saved') return undefined;
    const timer = setTimeout(() => setOfflineNoticeDismissed(true), 5000);
    return () => clearTimeout(timer);
  }, [storageState, selectedOfflineState]);

  const saveConversation = useCallback(async (messagesToSave) => {
    if (messagesToSave.length === 0) return;
    const savingAccount = localStorage.getItem('userId');
    const localSnapshot = await persistOffline(messagesToSave);
    if (!token || networkBlocked || networkBlockedRef.current || (nativeOffline && (!savingAccount || localStorage.getItem('userId') !== savingAccount))) { setPendingSave(false); return; }

    // Defensive: don't persist a conversation that has no user message — it
    // would end up titleless and unreadable when reopened.
    const hasUser = messagesToSave.some((m) => m && m.isUser);
    const hasAi   = messagesToSave.some((m) => m && !m.isUser);
    if (!hasUser || !hasAi) {
      console.warn("💾 Skipping save: incomplete conversation (hasUser=", hasUser, " hasAi=", hasAi, ")");
      setPendingSave(false);
      return;
    }

    console.log("💾 Sauvegarde conversation:", messagesToSave.length, "messages");

    const taggedMessages = messagesToSave.map((message) => ({
      ...message,
      agent_id: currentAgentId,
    }));

    try {
      const response = await axios.post(
        `${API_URL}/save-conversation`,
        {
          conversationId: conversationId?.startsWith('local:') ? null : conversationId,
          agent_id: currentAgentId,
          messages: taggedMessages,
          projectId: activeProject?.id || selectedProjectId || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const savedId = response.data.conversationId || conversationId;
      await confirmOffline(localSnapshot, savedId);
      // Adopt the server's id whenever it differs: a fork (someone else's shared
      // chat, or a WhatsApp one) returns a NEW id — keeping the old one made the
      // server fork again on every message.
      if (response.data.conversationId && response.data.conversationId !== conversationId) {
        setConversationIdForAgent(currentAgentId, response.data.conversationId);
        console.log("✅ Conversation ID reçu:", response.data.conversationId);
      }

      // ── Local fallback for project ↔ chat linkage ───────────────────
      // The deployed auth-api may not yet persist `projectId`. Mirror the
      // mapping in localStorage so the project page can still list its
      // chats correctly until the auth-api is redeployed.
      if (savedId) {
        try {
          const raw = localStorage.getItem('projectChatLinks');
          const map = raw ? JSON.parse(raw) : {};
          if (activeProject?.id) {
            map[savedId] = activeProject.id;
          } else {
            delete map[savedId];
          }
          localStorage.setItem('projectChatLinks', JSON.stringify(map));
        } catch (e) {
          console.warn('Could not update local project link map:', e);
        }
      }

      console.log("✅ Conversation sauvegardée avec succès");
      setPendingSave(false);
      setHistoryRefreshKey((k) => k + 1);
      onConversationSaved?.(savedId);
    } catch (err) {
      console.error('❌ Erreur sauvegarde conversation:', err);
      setPendingSave(false);
    }
  }, [activeProject?.id, selectedProjectId, conversationId, currentAgentId, onConversationSaved, setConversationIdForAgent, token, networkBlocked, nativeOffline, persistOffline, confirmOffline]);

  // Sync messages from parent — replace last AI message to handle both streaming tokens and final_answer
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const latest = messages[messages.length - 1];
    if (!latest || latest.isUser) return;
    if (embedded && ignoredParentMessageRef.current === latest) {
      ignoredParentMessageRef.current = null;
      return;
    }

    // Tag AI messages with the mode that produced them so the UI can show a badge
    const tagged = { ...latest, mode: latest.mode || chatMode };

    // `didChange` used to be assigned INSIDE the setChatMessages updater and
    // read on the next line. React only runs an updater eagerly when the
    // fiber's update queue is empty; during token streaming it almost never
    // is, so the updater ran later, `didChange` read false, and
    // setPendingSave(true) was skipped — the finished conversation was then
    // never saved. Decide from a ref instead, outside the updater.
    const signature = JSON.stringify([
      tagged.content, tagged.creditQuestionnaire, tagged.creditFlowStatus,
      tagged.riskSummary, tagged.repaymentSchedule,
    ]);
    const changed = lastAppliedSignatureRef.current !== signature;
    lastAppliedSignatureRef.current = signature;

    setChatMessages((prev) => {
      const lastMsg = prev[prev.length - 1];
      if (lastMsg && !lastMsg.isUser) {
        // The final text can match the last streamed token while also bringing
        // the next Finance question. Keep that structured result in the UI.
        if (lastMsg.content === tagged.content &&
            lastMsg.creditQuestionnaire === tagged.creditQuestionnaire &&
            lastMsg.creditFlowStatus === tagged.creditFlowStatus &&
            lastMsg.riskSummary === tagged.riskSummary &&
            lastMsg.repaymentSchedule === tagged.repaymentSchedule) return prev;
        return [...prev.slice(0, -1), tagged];
      }
      return [...prev, tagged];
    });
    if (changed) setPendingSave(true);
  }, [messages, chatMode, embedded]);

  // ✅ Sauvegarder uniquement quand la réponse est COMPLÈTE.
  // Use a ref guard so the effect can't fire saveConversation twice in a row
  // when its callback identity changes after `conversationId` is set.
  const savingInFlightRef = useRef(false);
  useEffect(() => {
    if (responseComplete && pendingSave && chatMessages.length > 0 && !savingInFlightRef.current) {
      savingInFlightRef.current = true;
      console.log("✅ Réponse complète détectée, sauvegarde...");
      Promise.resolve(saveConversation(chatMessages))
        .finally(() => { savingInFlightRef.current = false; });
    }
  }, [responseComplete, pendingSave, chatMessages, saveConversation]);

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const response = await axios.get(`${API_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response?.data?.plan) setUserPlan(response.data.plan);
        else setUserPlan('freemium');
      } catch {
        setUserPlan('freemium');
      } finally {
        setUserPlanLoaded(true);
      }
    };
    if (token && !networkBlocked) fetchUserPlan();
    else {
      setUserPlan('freemium');
      setUserPlanLoaded(true);
    }
  }, [token, networkBlocked]);

  useEffect(() => {
    const max = getMaxQuestions(userPlan);
    setMaxQuestions(max);
  }, [userPlan]);

  useEffect(() => {
    if (userPlan !== 'freemium') {
      localStorage.removeItem(STORAGE_KEY);
      setLimitReached(false);
      setRemaining(getMaxQuestions(userPlan));
    }
  }, [userPlan]);

  useEffect(() => {
    if (userPlan !== 'freemium') {
      setLimitReached(false);
      setRemaining(getMaxQuestions(userPlan));
      return;
    }
    const usage = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!usage || new Date() - new Date(usage.resetAt) > 86400000) {
      const initial = { count: 0, resetAt: new Date().toISOString() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
      setRemaining(getMaxQuestions(userPlan));
      setLimitReached(false);
    } else {
      const remainingQuestions = getMaxQuestions(userPlan) - usage.count;
      setRemaining(remainingQuestions);
      setLimitReached(usage.count >= getMaxQuestions(userPlan));
    }
  }, [maxQuestions, userPlan]);

  // Agentic dispatch: ask the backend which agent should handle the attached
  // image, then invoke the matching existing pipeline. 2.5s timeout + vision
  // fallback keep the UX identical to the old hardcoded behavior on failure.
  const routeAndDispatch = async (e) => {
    if (networkBlocked) return;
    let agent = 'vision';
    setIsRouting(true);
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 2500);
      const resp = await fetch(`${BACKEND_MAIN_URL}/route_agent`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          question,
          file_name: file?.name || '',
          file_mime: file?.type || '',
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (resp.ok) {
        const d = await resp.json();
        if (['vision', 'plantnet', 'labo'].includes(d.agent)) agent = d.agent;
      }
    } catch {
      /* router unreachable — vision fallback */
    } finally {
      // The handlers below set their own busy flag synchronously on entry,
      // so there is no gap in which a second Enter could slip through.
      setIsRouting(false);
    }
    if (agent === 'plantnet') return handlePlantNetIdentify(e, { auto: true });
    if (agent === 'labo') return handleLabReportAnalysis(e, { auto: true });
    return handleImageUpload(e, { auto: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (networkBlocked) return;
    if (domain === 'market' && !marketAvailable) return;
    if (!question.trim() && !file && chatFiles.length === 0) return;
    if (hasActiveCreditQuestion || isLoading || isUploadingFiles || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab || isRouting || limitReached) return;
    // State updates land on the next render, so two Enters before React
    // re-renders would both see "not busy". The ref closes that gap and is
    // released by the effect below once the busy state has been committed.
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    followLatestRef.current = true;

    // Dispatch based on mode
    if (file && isFileMode) {
      if (chatMode === 'vision') {
        handleImageUpload(e);
        return;
      }
      if (chatMode === 'plantnet') {
        handlePlantNetIdentify(e);
        return;
      }
      if (chatMode === 'labo') {
        handleLabReportAnalysis(e);
        return;
      }
    }

    // Image attached in DEFAULT mode → agentic routing: the backend decides
    // whether this is a vision / plantnet / labo job from the user's text +
    // file type, then we dispatch to the existing pipeline. Falls back to
    // vision if the router is slow or unreachable, so submit never hangs.
    if (file && chatMode === 'default' && file.type && file.type.startsWith('image/')) {
      routeAndDispatch(e);
      return;
    }

    if (!question.trim() && chatFiles.length === 0) return;

    if (userPlan === 'freemium') {
      const usage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
        count: 0, resetAt: new Date().toISOString(),
      };
      const now = new Date();
      const resetTime = new Date(usage.resetAt);
      if (now - resetTime > 86400000) {
        usage.count = 0;
        usage.resetAt = now.toISOString();
      }
      if (usage.count >= maxQuestions) {
        setLimitReached(true);
        return;
      }
      usage.count += 1;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
      setRemaining(maxQuestions - usage.count);
    } else {
      setLimitReached(false);
      setRemaining(getMaxQuestions(userPlan));
    }

    const attachedFilesText = chatMode === 'default' ? buildAttachedFilesText() : null;
    const userDisplay = chatMode === 'default' && chatFiles.length > 0
      ? `${question}\n\n📎 ${chatFiles.map((f) => f.name).join(', ')}`
      : question;
    const updated = [...chatMessages, { content: userDisplay, isUser: true }];
    setSelectedOfflineState('');
    setOfflineNoticeDismissed(false);
    setChatMessages(updated);
    void checkpointOffline(updated);
    onQuestionSubmit(question, {
      agentId: currentAgentId,
      frontendLang: i18n.language || 'fr',
      projectId: activeProject?.id || selectedProjectId || null,
      // #108: lets the backend leave THIS chat out of the project memory.
      conversationId,
      answerMode,
      projectInstructions: activeProject?.instructions || null,
      attachedFilesText,
      userMessage: userDisplay,
    });
    setQuestion('');
    setPendingSave(false);
    if (chatMode === 'default') setChatFiles([]);
  };

  const handleCreditQuestionSubmit = (answerText, creditPayload) => {
    if (networkBlocked) return;
    if (!answerText || !creditPayload) return;

    const updated = [...chatMessages, { content: answerText, isUser: true }];
    setSelectedOfflineState('');
    setOfflineNoticeDismissed(false);
    setChatMessages(updated);
    void checkpointOffline(updated);
    setPendingSave(false);

    onQuestionSubmit(answerText, {
      agentId: 'credit_agricole',
      userMessage: answerText,
      creditResponse: creditPayload,
      frontendLang: i18n.language || 'fr',
    });
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(t('chat.copied'));
    });
  };

  // Default-chat multi-file upload.
  //
  // We split incoming files by kind:
  //  • Images → set the single `file` state; on submit they go straight to the
  //    vision agent via handleImageUpload (same flow as Vision mode).
  //  • Text-bearing docs (PDF/DOCX/XLSX/CSV/TXT) → POST to /api/chat/upload,
  //    extract text server-side, append to `chatFiles` for context injection.
  // We intentionally accept one image at a time — the vision pipeline today
  // analyses a single photo. Newer image replaces the previous one.
  const uploadChatFiles = async (fileList) => {
    if (networkBlocked) return;
    if (!fileList || fileList.length === 0) return;
    if (chatMode !== 'default' || domain === 'market') return;

    const all = Array.from(fileList);
    const imageFile = all.find((f) => f.type && f.type.startsWith('image/'));
    const docFiles = all.filter((f) => !(f.type && f.type.startsWith('image/')));

    if (imageFile) {
      setFile(imageFile);
    }

    if (docFiles.length === 0) return;

    const slotsLeft = 5 - chatFiles.length;
    if (slotsLeft <= 0) {
      alert(t('chat.files_limit', 'Maximum 5 files per conversation.'));
      return;
    }
    const toSend = docFiles.slice(0, slotsLeft);
    const pendingFiles = toSend.map((localFile, index) => ({
      id: `pending-${Date.now()}-${index}-${Math.random()}`, name: localFile.name,
      size: localFile.size, mime_type: localFile.type, localFile, status: 'uploading',
    }));
    setPendingChatFiles((previous) => [...previous, ...pendingFiles]);
    const form = new FormData();
    toSend.forEach((f) => form.append('files', f, f.name));
    try {
      setIsUploadingFiles(true);
      const res = await axios.post(`${BACKEND_MAIN_URL}/api/chat/upload`, form, {
        headers: authHeaders({ 'Content-Type': 'multipart/form-data' }),
      });
      const items = (res.data && res.data.files) || [];
      const originals = [...toSend];
      const accepted = items.filter((it) => it && it.id && it.extracted_text).map((item) => {
        const index = originals.findIndex((original) => original.name === item.name && (item.size == null || original.size === item.size));
        const localFile = index >= 0 ? originals.splice(index, 1)[0] : null;
        return { ...item, localFile };
      });
      const rejected = items.filter((it) => it && it.error);
      if (rejected.length > 0) {
        alert(rejected.map((r) => `${r.name}: ${r.error}`).join('\n'));
      }
      if (accepted.length > 0) {
        setChatFiles((prev) => [...prev, ...accepted]);
      }
    } catch (err) {
      console.error('Chat file upload failed:', err);
      alert(t('chat.files_upload_error', 'File upload failed. Please try again.'));
    } finally {
      setIsUploadingFiles(false);
      const pendingIds = new Set(pendingFiles.map((item) => item.id));
      setPendingChatFiles((previous) => previous.filter((item) => !pendingIds.has(item.id)));
    }
  };

  const removeChatFile = (id) => {
    setChatFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const buildAttachedFilesText = () => {
    if (!chatFiles.length) return null;
    return chatFiles
      .map((f) => `--- File: ${f.name} ---\n${f.extracted_text || ''}`)
      .join('\n\n');
  };

  const handleChatFilesInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) uploadChatFiles(files);
    e.target.value = ''; // allow re-selecting the same file later
  };

  const handleChatDragOver = (e) => {
    if (chatMode !== 'default' || domain === 'market') return;
    e.preventDefault();
    setIsDraggingFiles(true);
  };
  const handleChatDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingFiles(false);
  };
  const handleChatDrop = (e) => {
    if (chatMode !== 'default' || domain === 'market') return;
    e.preventDefault();
    setIsDraggingFiles(false);
    const dropped = e.dataTransfer && e.dataTransfer.files;
    if (dropped && dropped.length > 0) uploadChatFiles(dropped);
  };

  const handleFileChange = (e) => {
    if (networkBlocked) return;
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type based on mode
    if (chatMode === 'labo') {
      // Allow image or pdf
      if (!selectedFile.type.startsWith('image/') && selectedFile.type !== 'application/pdf') {
        alert("Ce mode accepte uniquement les images et les PDF.");
        return;
      }
    } else if (chatMode === 'vision' || chatMode === 'plantnet') {
      // Allow only image
      if (!selectedFile.type.startsWith('image/')) {
        alert("Ce mode accepte uniquement les images.");
        return;
      }
    }

    setFile(selectedFile);
  };


  // Each domain has its own server-side history and saved conversation identity.
  // `keepDraft`: switching from the picker keeps what the user was typing, so
  // nothing is lost and no confirmation is needed (#120).
  const beginDomain = (nextDomain, { keepDraft = false } = {}) => {
    const nextAgent = agentForDomain(nextDomain);
    setDomain(nextDomain);
    setChatMode(modeForAgent(nextAgent));
    setSelectedTask(null);
    if (!keepDraft) setQuestion('');
    setFile(null);
    setChatFiles([]);
    setChatMessages([]); lastAppliedSignatureRef.current = null;
    setSelectedOfflineState('');
    setOfflineNoticeDismissed(false);
    setConversationIdForAgent(nextAgent, null);
    followLatestRef.current = true;
    onConversationLoad([], nextAgent);
    onAgentSessionReset?.(nextAgent);
  };

  const requestDomain = (nextDomain) => {
    if (nextDomain === domain || isLoading || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab || isUploadingFiles) return;
    if (nextDomain === 'finance' && !networkBlocked && !userPlanLoaded) return;
    if (nextDomain === 'finance' && !networkBlocked && !hasFinanceAccess) {
      beginDomain('agri');
      return;
    }
    // #120: switch straight away — the current conversation is already saved in
    // the history and the typed draft is kept, so there is nothing to confirm.
    beginDomain(nextDomain, { keepDraft: true });
  };

  const chooseTask = (task) => {
    if (task === 'case' && domain === 'finance') {
      if (!userPlanLoaded || isLoading || isUploadingFiles || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab || chatMessages.length > 0) return;
      if (!hasFinanceAccess) {
        beginDomain('agri');
        return;
      }
      // Switch from the finance normal chat to the structured guided assessment
      // and reset its session so the questionnaire always starts fresh (entering
      // the tab now opens 'finance_chat', so it no longer resets this session).
      setSelectedTask(task);
      setChatMode('credit');
      setConversationIdForAgent('credit_agricole', null);
      onAgentSessionResetRef.current?.('credit_agricole');
      // Prepare an editable draft. Only the normal submit action starts Finance.
      setQuestion((draft) => draft.trim() ? draft : t('chat.finance_intro_example_1'));
      questionInitialRef.current?.focus();
      return;
    }
    setSelectedTask(task);
    if (task === 'photo') fileInputRef.current?.click();
    else if (task === 'soil') chatFilesInputRef.current?.click();
    else questionInitialRef.current?.focus();
  };

  const handleImageUpload = async (e, opts = {}) => {
    e.preventDefault();
    if (networkBlocked) return;
    if (!file || isAnalyzing || isVisionStreaming) return;
    if (!canUseVisionNow()) { setShowVisionUpsell(true); return; }

    setIsAnalyzing(true);
    setIsVisionStreaming(true);
    const thumb = await fileToThumbnail(file);  // #94: keep the image visible on return
    // Show the user-side messages immediately so the UI leaves the welcome
    // view and the user can see their input was accepted while we wait on the API.
    setChatMessages((prev) => [
      ...prev,
      ...(question ? [{ content: question, isUser: true }] : []),
      { content: t('workspace:imageSentVision'), isUser: true, image: thumb },
    ]);
    const submittedQuestion = question;
    setQuestion('');
    // The message is already in the conversation, so empty the composer now
    // rather than in `finally` — the attachment used to sit in the box for
    // the whole 30-90s analysis, looking unsent.
    const submittedFile = file;
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    const formData = new FormData();
    formData.append('photo', submittedFile);
    if (submittedQuestion) formData.append('prompt', submittedQuestion);
    formData.append('chat_history', serializeChatHistory(chatMessages));
    formData.append('answer_mode', answerMode);  // #98: flash/detailed for image analysis

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // Extract user_id from JWT for language resolution
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.userId || payload.user_id || payload.sub;
        if (userId) headers['X-User-ID'] = userId;
        const jwt = localStorage.getItem('token');
        if (jwt) headers.Authorization = `Bearer ${jwt}`;
      } catch (e) {
        console.warn("Could not decode JWT:", e);
      }
    }

    // Streaming via SSE. Keep the existing "Analyse de la parcelle en cours…"
    // spinner up while the vision call is running, then swap to a streaming
    // bubble as soon as the first token arrives.
    let bubbleInserted = false;
    let visionSucceeded = false;
    setVisionPhase('vision');

    const insertBubble = (initial = '') => {
      if (bubbleInserted) return;
      bubbleInserted = true;
      setVisionPhase('');
      setIsAnalyzing(false);
      setChatMessages((prev) => [
        ...prev,
        { content: initial, isUser: false, mode: 'vision', auto: !!opts.auto },
      ]);
    };

    const appendToBubble = (chunk) => {
      setChatMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (!last || last.isUser) return prev;
        updated[updated.length - 1] = { ...last, content: (last.content || '') + chunk };
        return updated;
      });
    };

    try {
      const res = await fetch(`${BACKEND_MAIN_URL}/analyze_full_rag`, { method: 'POST', headers, body: formData });
      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => '');
        // Show the server's reason (e.g. "Unsupported file type (application/pdf)…"),
        // which used to be discarded in favour of a generic message.
        let serverMsg = '';
        try { serverMsg = JSON.parse(errText)?.error || ''; } catch { serverMsg = ''; }
        insertBubble(serverMsg || `Erreur (HTTP ${res.status})`);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sepIdx;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          if (!raw.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(raw.slice(6)); }
          catch { continue; }

          if (evt.type === 'token') {
            visionSucceeded = true;
            if (!bubbleInserted) insertBubble(evt.data);
            else appendToBubble(evt.data);
          } else if (evt.type === 'status') {
            // 'vision' = vision call running; 'rag' = RAG step starting
            setVisionPhase(evt.data || '');
          } else if (evt.type === 'error') {
            insertBubble(`Erreur lors de l'analyse de l'image : ${evt.data}`);
          } else if (evt.type === 'done') {
            // Trigger save once the stream completes.
            setChatMessages((prev) => {
              setTimeout(() => saveConversation(prev), 0);
              onChatHistorySync?.(prev, 'default');
              return prev;
            });
          }
          // 'timing' events are server-side only for now.
        }
      }

      if (!bubbleInserted) {
        insertBubble("Erreur lors de l'analyse de l'image.");
      }

      if (visionSucceeded && !hasVisionAccess) { consumeVision(); setShowVisionUpsell(true); }
    } catch (err) {
      insertBubble("Erreur lors de l'envoi de l'image.");
    } finally {
      setIsVisionStreaming(false);
      setIsAnalyzing(false);
      setVisionPhase('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePlantNetIdentify = async (e, opts = {}) => {
    e.preventDefault();
    if (networkBlocked) return;
    if (!file || isIdentifying) return;
    if (!canUseVisionNow()) { setShowVisionUpsell(true); return; }

    setIsIdentifying(true);
    const thumb = await fileToThumbnail(file);  // #94
    // Optimistic UI: show user-side messages before kicking off the API call.
    setChatMessages((prev) => [
      ...prev,
      ...(question ? [{ content: question, isUser: true }] : []),
      { content: t('workspace:imageSent'), isUser: true, image: thumb },
    ]);
    const submittedQuestion = question;
    setQuestion('');
    // The message is already in the conversation, so empty the composer now
    // rather than in `finally` — the attachment used to sit in the box for
    // the whole 30-90s analysis, looking unsent.
    const submittedFile = file;
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      const formData = new FormData();
      formData.append('photo', submittedFile);
      if (submittedQuestion) formData.append('prompt', submittedQuestion);
      if (plantnetOrgan) formData.append('organs', plantnetOrgan);
      formData.append('chat_history', serializeChatHistory(chatMessages));
      formData.append('answer_mode', answerMode);  // #98

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        // Extract user_id from JWT for language resolution
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userId = payload.userId || payload.user_id || payload.sub;
          if (userId) headers['X-User-ID'] = userId;
          const jwt = localStorage.getItem('token');
          if (jwt) headers.Authorization = `Bearer ${jwt}`;
        } catch (e) {
          console.warn("Could not decode JWT:", e);
        }
      }

      const res = await fetch(`${BACKEND_MAIN_URL}/identify_plantnet_rag`, { method: 'POST', headers, body: formData });
      const data = await res.json().catch(() => ({}));

      const plantAi = data.error
        ? { content: `Erreur d'identification : ${data.error}`, isUser: false, mode: 'plantnet', auto: !!opts.auto }
        : { content: data.ia_answer || `Erreur d'identification : HTTP ${res.status}`, isUser: false, mode: 'plantnet', auto: !!opts.auto };
      setChatMessages((prev) => {
        const next = [...prev, plantAi];
        setTimeout(() => saveConversation(next), 0);
        onChatHistorySync?.(next, 'default');
        return next;
      });

      if (res.ok && data.ia_answer && !hasVisionAccess) { consumeVision(); setShowVisionUpsell(true); }
    } catch {
      setChatMessages(prev => ([...prev, { content: `Erreur lors de l'identification.`, isUser: false, mode: 'plantnet' }]));
    } finally {
      setIsIdentifying(false);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLabReportAnalysis = async (e, opts = {}) => {
    e.preventDefault();
    if (networkBlocked) return;
    if (!file || isAnalyzingLab) return;
    if (!canUseVisionNow()) { setShowVisionUpsell(true); return; }

    setIsAnalyzingLab(true);
    const thumb = await fileToThumbnail(file);  // #94 (null for PDF lab reports)
    // Optimistic UI: show user-side messages before kicking off the API call.
    setChatMessages((prev) => [
      ...prev,
      ...(question ? [{ content: question, isUser: true }] : []),
      { content: t('workspace:labDocumentSent'), isUser: true, image: thumb },
    ]);
    const submittedQuestion = question;
    setQuestion('');
    // The message is already in the conversation, so empty the composer now
    // rather than in `finally` — the attachment used to sit in the box for
    // the whole 30-90s analysis, looking unsent.
    const submittedFile = file;
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      const formData = new FormData();
      formData.append('photo', submittedFile);
      if (submittedQuestion) formData.append('prompt', submittedQuestion);
      formData.append('chat_history', serializeChatHistory(chatMessages));
      formData.append('answer_mode', answerMode);  // #98

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userId = payload.userId || payload.user_id || payload.sub;
          if (userId) headers['X-User-ID'] = userId;
          const jwt = localStorage.getItem('token');
          if (jwt) headers.Authorization = `Bearer ${jwt}`;
        } catch (e) {
          console.warn("Could not decode JWT:", e);
        }
      }

      // The report is streamed over SSE (same transport as /analyze_full_rag).
      // The old blocking call stayed silent for 55-90s, so the connection was
      // dropped before the answer arrived and this always landed in catch().
      const res = await fetch(`${BACKEND_MAIN_URL}/analyze_lab_report`, { method: 'POST', headers, body: formData });
      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => '');
        // Show the server's reason (e.g. "Unsupported file type (application/pdf)…"),
        // which used to be discarded in favour of a generic message.
        let serverMsg = '';
        try { serverMsg = JSON.parse(errText)?.error || ''; } catch { serverMsg = ''; }
        const msg = serverMsg || `Erreur (HTTP ${res.status})`;
        setChatMessages((prev) => ([...prev, { content: msg, isUser: false, mode: 'labo', auto: !!opts.auto }]));
        return;
      }

      let labBubbleInserted = false;
      let labSucceeded = false;
      const upsertLabBubble = (chunk, replace = false) => {
        setChatMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (!labBubbleInserted || !last || last.isUser) {
            labBubbleInserted = true;
            return [...next, { content: chunk, isUser: false, mode: 'labo', auto: !!opts.auto }];
          }
          next[next.length - 1] = { ...last, content: replace ? chunk : (last.content || '') + chunk };
          return next;
        });
      };

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sepIdx;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          if (!raw.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(raw.slice(6)); } catch { continue; }

          if (evt.type === 'token') {
            labSucceeded = true;
            upsertLabBubble(evt.data);
          } else if (evt.type === 'error') {
            upsertLabBubble(`Erreur d'analyse : ${evt.data}`, !labSucceeded);
          } else if (evt.type === 'done') {
            setChatMessages((prev) => {
              setTimeout(() => saveConversation(prev), 0);
              onChatHistorySync?.(prev, 'default');
              return prev;
            });
          }
        }
      }

      if (!labBubbleInserted) upsertLabBubble("Erreur lors de l'analyse du rapport.");
      if (labSucceeded && !hasVisionAccess) { consumeVision(); setShowVisionUpsell(true); }
    } catch {
      setChatMessages(prev => ([...prev, { content: `Erreur lors de l'analyse du rapport.`, isUser: false }]));
    } finally {
      setIsAnalyzingLab(false);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clipTooltipText = (
    <div style={{ textAlign: 'left' }}>
      <b>{t('chat.title')}</b><br />
      • <i>{t('chat.analysis')}</i><br />
      • <i>{t('chat.identification')}</i><br />
      • <i>{t('chat.lab_analysis', 'Analyse résultats labo')}</i><br />
      {hasVisionAccess ? (
        <span>{t('chat.premium_access', { planName: visionPlanLabel })}</span>
      ) : (
        <span>
          {t('chat.limited_access')}{' '}
          <b>{t('chat.upgrade_link')}</b>{' '}
          {t('chat.unlimited')}
        </span>
      )}
    </div>
  );
  void clipTooltipText;

  const rootClassName = embedded
    ? `project-embedded-chat${chatMessages.length === 0 ? ' project-embedded-chat-empty' : ''}`
    : `chat-page${chatMessages.length === 0 ? ' chat-page-empty' : ''}`;
  const contentClassName = embedded ? 'project-embedded-chat-content' : 'main-content';
  const newChatButton = !embedded && (
    <button className="ws-icon-button" type="button" title={t('workspace:newChat')} aria-label={t('workspace:newChat')}
      onClick={() => navigate('/chat', { state: { conversation: null, timestamp: Date.now(), agentId: currentAgentId } })}>
      <FiEdit3 aria-hidden="true" />
    </button>
  );

  return (
    <div className={rootClassName}>
      {!embedded && (
      <SidebarNav onLogout={onLogout}>
        <ChatHistory
          agentId={currentAgentId}
          onSelectConversation={(conversation, agentId = 'default') => {
            if ((agentId === 'credit_agricole' || agentId === 'finance_chat') && !networkBlocked && !hasFinanceAccess) {
              beginDomain('agri');
              return;
            }
            setChatMode(modeForAgent(agentId));
            setDomain(domainForAgent(agentId));
            setSelectedTask(null);
            setQuestion('');
            setFile(null);
            setChatFiles([]);
            followLatestRef.current = true;
            setWhatsappConversationId(conversation?.source === 'whatsapp' ? conversation.id : null);
            if (conversation === null) {
              setSelectedOfflineState('');
              setOfflineNoticeDismissed(false);
              setSelectedProjectId(null);
              setChatMessages([]); lastAppliedSignatureRef.current = null;
              setConversationIdForAgent(agentId, null);
              onConversationLoad([], agentId);
              onAgentSessionReset?.(agentId);
              setActiveProject(null);
            } else {
              cacheOpenedConversation(conversation);
              const msgs = conversation.messages || [];
              setChatMessages(msgs);
              setConversationIdForAgent(agentId, conversation.id);
              onConversationLoad(msgs, agentId);
              // Restore project context if this conversation belongs to one
              // (also check the local fallback map until the auth-api is deployed)
              let pid = conversation.projectId || null;
              if (!pid) {
                try {
                  const map = JSON.parse(localStorage.getItem('projectChatLinks') || '{}');
                  pid = map[conversation.id] || null;
                } catch (_) {}
              }
              if (pid && !networkBlocked) {
                const userId = localStorage.getItem('userId');
                axios
                  .get(`${BACKEND_MAIN_URL}/api/projects/${pid}`, {
                    headers: { ...(userId ? { 'X-User-ID': userId } : {}), ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
                  })
                  .then((resp) => setActiveProject(resp.data))
                  .catch(() => setActiveProject(null));
              } else {
                setActiveProject(null);
              }
            }
          }}
          currentConversationId={conversationId}
          refreshTrigger={historyRefreshKey}
          onLogout={onLogout}
        />
      </SidebarNav>
      )}

      <div className={contentClassName}>
        {nativeOffline && chatMessages.length > 0 && (storageState || selectedOfflineState) && !offlineNoticeDismissed && <div className="ws-domain-notice ws-offline-notice" role="status">
          <span>{t(`workspace:offline_${storageState || selectedOfflineState}`)}</span>
          <button className="ws-icon-button" type="button" aria-label={t('workspace:close')} onClick={() => setOfflineNoticeDismissed(true)}><FaTimes aria-hidden="true" /></button>
        </div>}
        {!embedded && activeProject && (
          <div
            className="active-project-banner"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, padding: '10px 16px', margin: '10px 0',
              background: 'linear-gradient(135deg, #e8f1ef 0%, #f2f8f6 100%)',
              border: '1px solid #d4e4e0',
              borderRadius: 12,
              color: '#33312e', fontSize: '0.9rem',
              boxShadow: '0 1px 3px rgba(42,93,85,0.05)',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>📁</span>
              <span>
                {t('workspace:projectLabel')} <strong style={{ fontWeight: 700 }}>{activeProject.name}</strong>
                <span style={{ color: '#6b8a84', marginLeft: 8, fontWeight: 400 }}>
                  {activeProject.instructions ? t('workspace:context') : ''}
                </span>
              </span>
            </span>
            <span style={{ display: 'flex', gap: 6 }}>
              <a
                href={`/projects/${activeProject.id}`}
                style={{
                  padding: '5px 12px', borderRadius: 999,
                  border: 'none', background: '#7DADA4', color: '#fff',
                  cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                {t('workspace:open')}
              </a>
              <a
                href="/chat"
                title="Leave project"
                style={{
                  padding: '5px 12px', borderRadius: 999,
                  border: '1px solid #d4e4e0', background: '#fff',
                  color: '#6b8a84', cursor: 'pointer', fontSize: '0.82rem',
                  textDecoration: 'none',
                }}
              >
                {t('workspace:exit')}
              </a>
            </span>
          </div>
        )}

        <div className="question-limit-info">
          {limitReached ? (<p className="limit-msg">{t('chat.limit_reached')}</p>) : (<p className="remaining-msg">{userPlan === 'freemium' ? t('workspace:quota', { count: remaining }) : ''}</p>)}
        </div>

        <div className={`ws-chat-layout${!embedded ? ' ws-chat-layout-rail' : ''}`}>
        {chatMessages.length === 0 ? (
          <div className="chat-with-sidebar" style={{ display: 'flex', gap: 16, alignItems: 'stretch', width: '100%', minHeight: '60vh' }}>
          <div className="initial-chat">
            <div className="ws-chat-intro">
              {embedded ? <h2>{t('workspace:projectWelcome')}</h2> : <div className="ws-chat-heading"><h1>{t('workspace:welcome')}</h1>{newChatButton}</div>}
              {embedded && <p>{t('workspace:projectWelcomeHint')}</p>}
            </div>
            <DomainPicker value={domain} onChange={requestDomain} allowFinance={allowFinanceDomain} disabled={isLoading || isUploadingFiles} />
            {domain === 'market' && !marketAvailable && <p className="ws-domain-notice" role="status">{t('workspace:marketSoon')}</p>}
            {selectedTask && <div className="ws-task-context">
              <div><strong>{t(`workspace:task_${selectedTask}`)}</strong><p>{t(`workspace:taskHint_${selectedTask}`)}</p></div>
              <button className="ws-icon-button" type="button" onClick={() => setSelectedTask(null)} aria-label={t('workspace:close')}><FaTimes /></button>
            </div>}
            <ChatComposer question={question} setQuestion={setQuestion} inputRef={questionInitialRef}
              exampleLabel={domain === 'finance' && selectedTask === 'case' && question === t('chat.finance_intro_example_1') ? t('workspace:financeExampleLabel') : undefined}
              onSubmit={handleSubmit} onKeyDown={handleQuestionKeyDown}
              disabled={!networkBlocked && (limitReached || hasActiveCreditQuestion)} busy={isLoading || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab || isRouting}
              placeholder={hasActiveCreditQuestion ? t('chat.credit_pending_placeholder') : selectedTask ? t(`workspace:taskHint_${selectedTask}`) : t('workspace:conversationHint')}
              answerMode={answerMode} onAnswerModeChange={changeAnswerMode}
              mode={chatMode} submissionUnavailable={networkBlocked || (domain === 'market' && !marketAvailable)} structured={hasActiveCreditQuestion}
              file={file} onRemoveFile={() => setFile(null)} files={chatFiles} onRemoveDocument={removeChatFile}
              uploading={isUploadingFiles} pendingFiles={pendingChatFiles} isFileMode={isFileMode} fileInputRef={fileInputRef} documentsInputRef={chatFilesInputRef}
              onFileChange={handleFileChange} onDocumentsChange={handleChatFilesInputChange} accept={CHAT_FILE_ACCEPT}
              onDragOver={handleChatDragOver} onDragLeave={handleChatDragLeave} onDrop={handleChatDrop} dragging={isDraggingFiles}
              organ={plantnetOrgan} setOrgan={setPlantnetOrgan} />
            <DomainTasks domain={domain} selected={selectedTask} onSelect={chooseTask}
              disabled={networkBlocked || isLoading || isUploadingFiles || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab} />
          </div>
          </div>
        ) : (
          <div className="chat-with-sidebar" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', width: '100%' }}>
          <section className="chat-container" aria-label={t('workspace:discussions')}>
            <div className="ws-conversation-context">
              <DomainPicker compact value={domain} onChange={requestDomain} allowFinance={allowFinanceDomain}
                disabled={isLoading || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab || isUploadingFiles} />
              {chatMode === 'credit' && chatMessages.some((message) => message.creditQuestionnaire) &&
                <button type="button" className="ws-text-button" onClick={() => setShowEvaluationPoints(true)}>{t('workspace:evaluationPoints')}</button>}
              {newChatButton}
            </div>
            {whatsappConversationId && whatsappConversationId === conversationId && (
              <p className="ws-whatsapp-notice" role="note">
                <FaWhatsapp aria-hidden="true" /> {t('workspace:whatsappNotice')}
              </p>
            )}
            <div className="chat-messages" ref={messagesPanelRef} onScroll={(event) => { const panel = event.currentTarget; followLatestRef.current = panel.scrollHeight - panel.scrollTop - panel.clientHeight < 80; }}>
              {chatMessages.map((msg, index) => {
                // #94: an image message shows the small picture itself, not the
                // "[Image sent — …]" placeholder (kept in `content` for history/titles).
                if (msg.isUser && msg.image) return <div key={index} className="message user-message ws-image-message">
                  <img className="ws-message-image" src={msg.image} alt={msg.content || ''} />
                </div>;
                if (msg.isUser) return <div key={index} className="message user-message">{msg.content}</div>;
                const full = domain === 'market' ? msg.content || '' : stripReferenceSections(msg.content || '');
                const { coreMarkdown, transparencyMarkdown } = splitTransparencySection(full);
                const isStreaming = isLoading && index === chatMessages.length - 1;
                const isActiveQuestion = Boolean(msg.creditQuestionnaire) && index === chatMessages.length - 1;
                return (
                  <div key={index} className="message ai-message">
                    <div className="ai-sender-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span className="ws-answer-brand"><img src="/Logo.png" alt="" /> Upya</span>
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(coreMarkdown) }} />
                    {isStreaming && <span className="typing-cursor" />}
                    {msg.creditQuestionnaire && (isActiveQuestion ? <section className="ws-evaluation-step" aria-label={t('workspace:evaluationTitle')}>
                      <div className="ws-evaluation-heading"><span>{t('workspace:evaluationTitle')}</span><span>{t('workspace:evaluationStep', { count: chatMessages.slice(0, index + 1).filter((item) => item.creditQuestionnaire).length })}</span></div>
                      {msg.creditQuestionnaire.input_type === 'review_table'
                        ? <FinanceReviewCard review={msg.creditQuestionnaire} disabled={networkBlocked || isLoading} onSubmit={handleCreditQuestionSubmit} t={t} />
                        : <CreditQuestionCard questionnaire={msg.creditQuestionnaire} disabled={networkBlocked || isLoading} onSubmit={handleCreditQuestionSubmit} t={t} />}
                    </section> : <details className="ws-evaluation-previous"><summary>{msg.creditQuestionnaire.question}</summary>
                      {msg.creditQuestionnaire.helper_text && <p>{msg.creditQuestionnaire.helper_text}</p>}
                    </details>)}
                    {transparencyMarkdown && (
                      <div className="transparency-block" style={{ marginTop: '12px' }}>
                        <hr style={{ opacity: 0.3 }} />
                        <div dangerouslySetInnerHTML={{ __html: renderMarkdown(transparencyMarkdown) }} />
                      </div>
                    )}
                    {!isStreaming && (
                      <div className="ai-actions" style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button className="copy-button" onClick={() => handleCopy(full)}>{t('chat.copy')}</button>
                      </div>
                    )}
                  </div>
                );
              })}
              {isAnalyzing && (
                <div className="message ai-message thinking-container">
                  <img src="/Logo.png" alt="Gaia Logo" className="thinking-logo" />
                  <p className="thinking-text">
                    {visionPhase === 'rag'
                      ? t('chat.analyzing_vision_rag', 'Recherche des sources scientifiques...')
                      : visionPhase === 'vision'
                        ? t('chat.analyzing_vision_image', "Lecture de l'image...")
                        : t('chat.analyzing_vision', 'Analyse de la parcelle en cours...')}
                  </p>
                </div>
              )}
              {isIdentifying && (
                <div className="message ai-message thinking-container">
                  <img src="/Logo.png" alt="Gaia Logo" className="thinking-logo" />
                  <p className="thinking-text">{t('chat.identifying_plant', 'Identification de la plante...')}</p>
                </div>
              )}
              {isAnalyzingLab && (
                <div className="message ai-message thinking-container">
                  <img src="/Logo.png" alt="Gaia Logo" className="thinking-logo" />
                  <p className="thinking-text">{t('chat.analyzing_lab', 'Analyse du rapport...')}</p>
                </div>
              )}
              {isLoading && (chatMessages.length === 0 || chatMessages[chatMessages.length - 1].isUser) && (
                <>
                  <div className="message ai-message thinking-container">
                    <img src="/Logo.png" alt="Gaia Logo" className="thinking-logo" />
                    <p className="thinking-text">{t('chat.thinking')}</p>
                  </div>
                  {streamedResponse && (
                    <div className="message ai-message">
                      <div dangerouslySetInnerHTML={{ __html: renderMarkdown(stripReferenceSections(streamedResponse)) }} />
                    </div>
                  )}
                </>
              )}
            </div>

            <ChatComposer compact question={question} setQuestion={setQuestion} inputRef={questionChatRef}
              onSubmit={handleSubmit} onKeyDown={handleQuestionKeyDown}
              disabled={!networkBlocked && (limitReached || hasActiveCreditQuestion)} busy={isLoading || isAnalyzing || isVisionStreaming || isIdentifying || isAnalyzingLab || isRouting}
              placeholder={hasActiveCreditQuestion ? t('chat.credit_pending_placeholder') : selectedTask ? t(`workspace:taskHint_${selectedTask}`) : t('workspace:conversationHint')}
              answerMode={answerMode} onAnswerModeChange={changeAnswerMode}
              mode={chatMode} submissionUnavailable={networkBlocked || (domain === 'market' && !marketAvailable)} structured={hasActiveCreditQuestion}
              file={file} onRemoveFile={() => setFile(null)} files={chatFiles} onRemoveDocument={removeChatFile}
              uploading={isUploadingFiles} pendingFiles={pendingChatFiles} isFileMode={isFileMode} fileInputRef={fileInputRef} documentsInputRef={chatFilesInputRef}
              onFileChange={handleFileChange} onDocumentsChange={handleChatFilesInputChange} accept={CHAT_FILE_ACCEPT}
              onDragOver={handleChatDragOver} onDragLeave={handleChatDragLeave} onDrop={handleChatDrop} dragging={isDraggingFiles}
              organ={plantnetOrgan} setOrgan={setPlantnetOrgan} />
          </section>
          </div>
        )}
        {!embedded && !networkBlocked && <div className="ws-resources">
          <aside className="ws-weather-rail"><WeatherCard compact /></aside>
          <div className="ws-chat-whatsapp"><WhatsAppInvitation /></div>
        </div>}
        </div>
      </div>

      {showVisionUpsell && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
          onClick={() => setShowVisionUpsell(false)}
        >
          <div
            className="modal-card"
            style={{ background: '#fff', borderRadius: 12, padding: 20, width: 'min(520px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 10 }}>{t('workspace:visionLimitTitle')}</h3>
            <p style={{ margin: '8px 0 16px' }}>
              {nativeOffline ? t('workspace:mobileVisionLimit') : t('workspace:visionLimitBody', { plan: userPlan })}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowVisionUpsell(false)} style={{ padding: '8px 12px' }}>
                {t(nativeOffline ? 'workspace:close' : 'chat.finance_upsell_later')}
              </button>
              {!nativeOffline && <a href={SUBSCRIPTION_URL} style={{ textDecoration: 'none' }}>
                <button
                  style={{
                    padding: '8px 14px',
                    background: '#33312e', color: '#fff', border: '1px solid #1f1e1c',
                    borderRadius: 8, fontWeight: 600
                  }}
                >
                  {t('workspace:visionUpgrade')}
                </button>
              </a>}
            </div>
          </div>
        </div>
      )}
      {/* The Finance upsell dialog was removed: users without the plan no
          longer see Finance anywhere in the UI (tab hidden, entry points fall
          back to Agri), so there is nothing to upsell from. */}
      {showEvaluationPoints && <WorkspaceDialog title={t('workspace:evaluationTitle')} onClose={() => setShowEvaluationPoints(false)}>
        <div className="ws-form"><p className="ws-muted">{t('workspace:evaluationPointsHint')}</p>
          <ol className="ws-evaluation-points">{chatMessages.filter((message) => message.creditQuestionnaire).map((message, index) =>
            <li key={`${message.creditQuestionnaire.id}-${index}`}>{message.creditQuestionnaire.question}</li>)}</ol>
        </div>
      </WorkspaceDialog>}
    </div>
  );
};

export default Chat;
